import React, { useState, useEffect, useRef } from 'react';
import { 
  XMarkIcon, 
  PaperAirplaneIcon, 
  SparklesIcon,
  DocumentTextIcon 
} from '@heroicons/react/24/outline';
import { useMeeting } from '../contexts/MeetingContext';
import { apiService } from '../services/api';

// Get API base URL from environment or default
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Helper function to construct full backend URLs
const getBackendUrl = (path) => {
  // For production, use the backend domain directly
  if (!API_BASE_URL.startsWith('/')) {
    return `${API_BASE_URL}${path}`;
  }
  // For local development, use localhost:8000
  return `http://localhost:8000${API_BASE_URL}${path}`;
};

const AgendaChatPanel = ({ onCitationClick }) => {
  const { 
    selectedMeeting: meeting, 
    selectedAgenda: agendaItem,
    chatOpen,
    setChatOpen,
    chatMessages: messages,
    setChatMessages: setMessages 
  } = useMeeting();
  
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Load suggested questions on mount
  useEffect(() => {
    if (chatOpen) {
      loadSuggestedQuestions();
    }
  }, [meeting, agendaItem, chatOpen]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatOpen) {
      scrollToBottom();
    }
  }, [messages, chatOpen]);
  
  if (!chatOpen) return null;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSuggestedQuestions = async () => {
    try {
      setLoadingSuggestions(true);
      const url = getBackendUrl(`/agenda/suggested-questions?meeting=${encodeURIComponent(meeting)}&agenda=${encodeURIComponent(agendaItem)}`);
      const response = await fetch(url);
      const data = await response.json();
      setSuggestedQuestions(data.questions || []);
    } catch (error) {
      console.error('Error loading suggested questions:', error);
      setSuggestedQuestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const sendMessage = async (questionText) => {
    const question = questionText || input.trim();
    if (!question || isLoading) return;

    // Clear input
    setInput('');

    // Add user message
    const userMessage = { role: 'user', content: question };
    setMessages(prev => [...prev, userMessage]);

    // Start assistant message with "Thinking..." placeholder
    const assistantMessage = { role: 'assistant', content: 'Thinking...', citations: [], isThinking: true };
    setMessages(prev => [...prev, assistantMessage]);

    setIsLoading(true);

    try {
      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Build chat history from previous messages only
      // Filter out thinking/error messages and only include completed messages
      const chatHistory = messages
        .filter(msg => 
          !msg.isThinking && 
          !msg.isError && 
          msg.content && 
          msg.content !== 'Thinking...'
        )
        .map(msg => ({
          role: msg.role,
          content: msg.content
        }));

      console.log('[CHAT] Sending chat history with', chatHistory.length, 'messages');

      const url = getBackendUrl(`/agenda/chat?meeting=${encodeURIComponent(meeting)}&agenda=${encodeURIComponent(agendaItem)}`);
      const response = await fetch(
        url,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            question,
            chat_history: chatHistory
          }),
          signal: abortControllerRef.current.signal
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim()) continue;

          try {
            const data = JSON.parse(line);

            if (data.type === 'token') {
              // Append token to the last message
              setMessages(prev => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                const lastMsg = updated[lastIndex];
                if (lastMsg && lastMsg.role === 'assistant') {
                  // Create a new object instead of mutating
                  // If content is "Thinking...", replace it with the first token
                  const newContent = lastMsg.content === 'Thinking...' 
                    ? data.content 
                    : lastMsg.content + data.content;
                  
                  updated[lastIndex] = {
                    ...lastMsg,
                    content: newContent,
                    isThinking: false
                  };
                }
                return updated;
              });
            } else if (data.type === 'done') {
              // Add citations to the last message
              setMessages(prev => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                const lastMsg = updated[lastIndex];
                if (lastMsg && lastMsg.role === 'assistant') {
                  // Create a new object instead of mutating
                  updated[lastIndex] = {
                    ...lastMsg,
                    citations: data.citations || []
                  };
                }
                return updated;
              });
            } else if (data.type === 'error') {
              // Show error message
              setMessages(prev => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                const lastMsg = updated[lastIndex];
                if (lastMsg && lastMsg.role === 'assistant') {
                  // Create a new object instead of mutating
                  updated[lastIndex] = {
                    ...lastMsg,
                    content: `Error: ${data.content}`,
                    isError: true
                  };
                }
                return updated;
              });
            }
          } catch (e) {
            console.error('Error parsing stream data:', e);
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request aborted');
        return;
      }
      console.error('Error sending message:', error);
      setMessages(prev => {
        const updated = [...prev];
        const lastMsg = updated[updated.length - 1];
        if (lastMsg && lastMsg.role === 'assistant') {
          lastMsg.content = 'Sorry, there was an error processing your request. Please try again.';
          lastMsg.isError = true;
        }
        return updated;
      });
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleSuggestedQuestion = (question) => {
    sendMessage(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleCitationClickInternal = (doc, page) => {
    if (onCitationClick) {
      onCitationClick(doc, page);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 w-[450px] h-[650px] bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col z-50 animate-slideUp">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 px-4 py-3 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-white" />
          <h3 className="font-semibold text-white">AI Assistant</h3>
        </div>
        <button
          onClick={() => setChatOpen(false)}
          className="hover:bg-red-700/50 p-1 rounded transition-colors text-white"
          title="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Suggested Questions (only show if no messages yet) */}
      {messages.length === 0 && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-600 mb-2 font-medium">Suggested questions:</p>
          {loadingSuggestions ? (
            <div className="text-xs text-gray-400">Loading suggestions...</div>
          ) : (
            <div className="space-y-2">
              {suggestedQuestions.map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestedQuestion(question)}
                  className="w-full text-left text-xs bg-white border border-gray-200 rounded px-3 py-2 hover:bg-red-50 hover:border-red-200 transition-colors text-gray-700"
                  disabled={isLoading}
                >
                  {question}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && !loadingSuggestions && (
          <div className="text-center text-gray-400 mt-8">
            <SparklesIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Ask me anything about this agenda item!</p>
            <p className="text-xs mt-2">I can help you understand proposals, identify consensus, and analyze positions.</p>
          </div>
        )}

        {messages.map((message, idx) => (
          <ChatMessage
            key={idx}
            message={message}
            onCitationClick={handleCitationClickInternal}
          />
        ))}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 p-4 bg-gray-50">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about this agenda..."
            className="flex-1 resize-none border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            rows="2"
            disabled={isLoading}
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || isLoading}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors self-end"
            title="Send"
          >
            {isLoading ? (
              <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <PaperAirplaneIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ChatMessage = ({ message, onCitationClick }) => {
  const isUser = message.role === 'user';
  const isError = message.isError;
  const isThinking = message.isThinking;

  // Parse inline citations and make them clickable
  const parseInlineCitations = (text) => {
    if (!text) return [];
    
    // Extended regex to match:
    // 1. [R2-2506968, page 1] or [R3-1234, p.5] - formal citations with page (for backward compatibility)
    // 2. [R2-2506968] - citations without page
    // 3. R2-2506968 - bare document IDs (not in brackets)
    const citationRegex = /\[([RS]\d-\d+)(?:,\s*(?:page|p\.?)\s*(\d+))?\]|([RS]\d-\d+)/g;
    
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = citationRegex.exec(text)) !== null) {
      // Add text before citation
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      
      // Extract document ID and page number
      const document = match[1] || match[3]; // Either bracketed or bare
      const page = match[2] || '1'; // Use provided page or default to 1
      
      // Add citation - display just the document ID without page
      parts.push({
        type: 'citation',
        document: document,
        page: page,
        displayText: document // Only show document ID, not page
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    return parts;
  };

  // Format text with bold numbers, red bullets, and clickable citations
  const formatText = (text) => {
    if (!text) return null;
    
    const lines = text.split('\n');
    return lines.map((line, lineIdx) => {
      // Parse inline citations in this line
      const parts = parseInlineCitations(line);
      
      // Check if line starts with a number (e.g., "1. ", "2. ", etc.)
      const numberMatch = line.match(/^(\d+\.\s)(.*)$/);
      if (numberMatch) {
        const restParts = parseInlineCitations(numberMatch[2]);
        return (
          <div key={lineIdx} className="block">
            <strong>{numberMatch[1]}</strong>
            {restParts.map((part, partIdx) => 
              part.type === 'citation' ? (
                <button
                  key={partIdx}
                  onClick={() => onCitationClick(part.document, parseInt(part.page))}
                  className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer mx-0.5"
                  title={`View ${part.document}`}
                >
                  <DocumentTextIcon className="h-3 w-3 inline" />
                  {part.displayText}
                </button>
              ) : (
                <span key={partIdx}>{part.content}</span>
              )
            )}
          </div>
        );
      }
      
      // Check if line starts with dash bullet
      const bulletMatch = line.match(/^(\s*)-\s(.*)$/);
      if (bulletMatch) {
        const restParts = parseInlineCitations(bulletMatch[2]);
        return (
          <div key={lineIdx} className="block flex">
            <span className="text-red-600 mr-2">{bulletMatch[1]}•</span>
            <span>
              {restParts.map((part, partIdx) => 
                part.type === 'citation' ? (
                  <button
                    key={partIdx}
                    onClick={() => onCitationClick(part.document, parseInt(part.page))}
                    className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer mx-0.5"
                    title={`View ${part.document}`}
                  >
                    <DocumentTextIcon className="h-3 w-3 inline" />
                    {part.displayText}
                  </button>
                ) : (
                  <span key={partIdx}>{part.content}</span>
                )
              )}
            </span>
          </div>
        );
      }
      
      // Regular line with potential citations
      if (parts.length === 0) {
        return line ? <div key={lineIdx} className="block">{line}</div> : <br key={lineIdx} />;
      }
      
      return (
        <div key={lineIdx} className="block">
          {parts.map((part, partIdx) => 
            part.type === 'citation' ? (
              <button
                key={partIdx}
                onClick={() => onCitationClick(part.document, parseInt(part.page))}
                className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer mx-0.5"
                title={`View ${part.document}`}
              >
                <DocumentTextIcon className="h-3 w-3 inline" />
                {part.displayText}
              </button>
            ) : (
              <span key={partIdx}>{part.content}</span>
            )
          )}
        </div>
      );
    });
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-lg border shadow-sm px-4 py-3 ${
        isUser 
          ? 'bg-red-600 text-white border-red-700' 
          : isError
            ? 'bg-red-50 border-red-200 text-red-800'
            : 'bg-white border-gray-200 text-gray-900'
      }`}>
        <div className="text-sm break-words leading-relaxed">
          {isUser ? (
            <div className="whitespace-pre-wrap">{message.content}</div>
          ) : isThinking ? (
            <div className="italic text-gray-500 animate-pulse">Thinking...</div>
          ) : (
            formatText(message.content)
          )}
        </div>
        
        {/* Citations */}
        {message.citations && message.citations.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
            <p className="text-xs font-semibold text-gray-600 mb-2">Sources:</p>
            {message.citations.map((citation, idx) => (
              <button
                key={idx}
                onClick={() => onCitationClick(citation.document, citation.page)}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                <DocumentTextIcon className="h-3 w-3" />
                {citation.document}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgendaChatPanel;

